import { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";

import FinalReport from "./report";
import Transaction from "./transaction-details";
import PropertyInfo from "./property-infor";
import ClientCounterparty from "./ClientCounterparty";
import CorporateStructure from "./CorporateStructure";
import ManagementStructureForm from "./management-structure";
import RiskAsses from "./risk-assement";
import RiskAssesContinue from "./riskassessmentContinue";

import { sharedData } from "../sharedData/sharedData";
import config from "../config/config";
import { userSession } from "../observers/userObserver";
import { riskAssesContObservable } from "../observers/riskAssesContObservable";
import './pdf-styles.css';

export default function Report3() {
  const requestId = sharedData.get("requestId");
  const [isGenerating, setIsGenerating] = useState(false);
  const [logMessage, setLogMessage] = useState('');

  const summaryRef = useRef();
  const transactionRef = useRef();
  const propertyRef = useRef();
  const counterpartyRef = useRef();
  const corporateRef = useRef();
  const managementRef = useRef();
  const risk1Ref = useRef();
  const risk2Ref = useRef();

  useEffect(() => {
    const hasReloaded = localStorage.getItem("reportReloaded");

    if (!(hasReloaded === "true")) {
      localStorage.setItem("reportReloaded", "true");

      // Refresh after a short delay (e.g., 100ms)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
}, []);

  const convertSvgsToPngs = async (element) => {
    const svgs = element.querySelectorAll('svg');
    if (!svgs.length) return;
    await Promise.all(Array.from(svgs).map(svg => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const xml = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([xml], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const png = document.createElement('img');
          png.src = canvas.toDataURL('image/png');
          png.width = img.width;
          png.height = img.height;
          svg.parentNode.replaceChild(png, svg);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = url;
      });
    }));
  };

  const renderToImageAndAddToPdf = async (pdf, element, isFirstPage = false) => {
    await convertSvgsToPngs(element);
    await new Promise(resolve => requestAnimationFrame(resolve)); // wait for render

    const canvas = await html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      scrollY: -window.scrollY,
      windowWidth: 1024,
    });

    const imgData = canvas.toDataURL('image/jpeg');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    if (!isFirstPage) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
  };

  const generateSummaryPdf = async () => {
  setLogMessage("Generating Summary PDF...");

  const pdf = new jsPDF('p', 'pt', 'a4'); // A4 size
  const element = summaryRef.current;

  await convertSvgsToPngs(element);
  await new Promise(resolve => requestAnimationFrame(resolve)); // ensure layout is complete

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    scrollY: -window.scrollY,
    backgroundColor: '#ffffff'
  });

  const imgData = canvas.toDataURL('image/jpeg', 1.0);
  const pdfWidth = pdf.internal.pageSize.getWidth();    // ~595.28 pt
  const pdfHeight = pdf.internal.pageSize.getHeight();  // ~841.89 pt

  // Get aspect ratio of the canvas
  const canvasAspectRatio = canvas.width / canvas.height;
  const pdfAspectRatio = pdfWidth / pdfHeight;

  let imgWidth, imgHeight;

  if (canvasAspectRatio > pdfAspectRatio) {
    // Wider than A4 — fit width
    imgWidth = pdfWidth;
    imgHeight = canvas.height * (pdfWidth / canvas.width);
  } else {
    // Taller than A4 — fit height
    imgHeight = pdfHeight;
    imgWidth = canvas.width * (pdfHeight / canvas.height);
  }

  const x = (pdfWidth - imgWidth) / 2;
  const y = (pdfHeight - imgHeight) / 2;

  pdf.addImage(imgData, 'JPEG', x, y, imgWidth, imgHeight);
  return pdf.output('blob');
};


 const isRiskAssesContinue = () => {
  const currentList = localStorage.getItem("riskAssesContObservable");
  if (currentList === null || currentList === undefined || currentList.length === 0) {
      return false;
    }
    return currentList.some(obj => obj.value === true);
 }

  const generateDetailedPdf = async () => {
    setLogMessage("Generating Detailed PDF...");
    const pdf = new jsPDF('p', 'pt', 'a4');
    const sections = [
      transactionRef,
      propertyRef,
      counterpartyRef,
      corporateRef,
      managementRef,
      risk1Ref,
      // risk2Ref
    ];

    if (isRiskAssesContinue()) {
      sections.push(risk2Ref);
    }

    for (let i = 0; i < sections.length; i++) {
      await renderToImageAndAddToPdf(pdf, sections[i].current, i === 0);
    }
    return pdf.output('blob');
  };

  const fetchAndExtractZipFiles = async () => {
    setLogMessage("Fetching Uploaded Documents...");
    const response = await fetch(`${config['backend-url']}/get_blob_zip_by_request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userSession.getToken() || ''}`
      },
      body: JSON.stringify({ requestId }),
    });
    if (!response.ok) throw new Error('Failed to fetch ZIP files');

    const zipBlob = await response.blob();
    const zip = await JSZip.loadAsync(zipBlob);
    const fileEntries = [];

    await Promise.all(Object.values(zip.files).map(async (file) => {
      if (!file.dir) {
        const blob = await file.async('blob');
        fileEntries.push({ name: file.name, blob });
      }
    }));

    return fileEntries;
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    setLogMessage("Starting report generation...");

    try {
      const [summaryPdf, detailedPdf, uploadedFiles] = await Promise.all([
        generateSummaryPdf(),
        generateDetailedPdf(),
        fetchAndExtractZipFiles()
      ]);

      const zip = new JSZip();
      zip.file(`Summary_Report_${requestId}.pdf`, summaryPdf);
      zip.file(`Detailed_Report_${requestId}.pdf`, detailedPdf);
      uploadedFiles.forEach(({ name, blob }) => {
        zip.file(name, blob);
      });

      const finalZip = await zip.generateAsync({ type: 'blob' });

      const url = URL.createObjectURL(finalZip);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Detailed Report Request ${requestId}.zip`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setLogMessage("Download complete!");
    } catch (err) {
      console.error(err);
      setLogMessage("Error occurred during generation.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleDownload}
        disabled={isGenerating}
        style={{
          marginLeft: "20px",
          marginTop: "100px",
          padding: "10px",
          backgroundColor: "#07b1b0",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer"
        }}
      >
        {isGenerating ? "Generating..." : "Download Report Package"}
      </button>

      {isGenerating && (
        <h3 style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          {logMessage}
        </h3>
      )}

      {/* Offscreen Render Area */}
      <div className="pdf-render-container">
        <div ref={summaryRef}><FinalReport isReport={true} /></div>
        <div ref={transactionRef}><Transaction isReport={true} /></div>
        <div ref={propertyRef}><PropertyInfo isReport={true} /></div>
        <div ref={counterpartyRef}><ClientCounterparty isReport={true} /></div>
        <div ref={corporateRef}><CorporateStructure isReport={true} /></div>
        <div ref={managementRef}><ManagementStructureForm isReport={true} /></div>
        <div ref={risk1Ref}><RiskAsses isReport={true} /></div>
        <div ref={risk2Ref}><RiskAssesContinue isReport={true} /></div>
      </div>
    </div>
  );
}
